import React, { useState } from "react";
import Table from "../../../../../../common/element/Table";
import Dialog from "../../../../../../common/element/Dialog";
import Label from "../../../../../../common/element/Label";
import Button from "../../../../../../common/element/Buttons";
import moment from "moment";
import RichTextEditor from "../../../../../../common/snippet/RichTextEditor";
import EmailImg from "../../../../../../assets/icons-svg/email.svg";
import BtnLight from "../../../../../../common/element/Buttons/btnLight";

const formatActionName = (action) => {
  if (!action) return "-";

  const overrides = {
    "2fa-otp": "2FA OTP Request",
    "2fa_otp_request": "2FA OTP Request",
    "_mobile-2fa-otp": "Mobile 2FA OTP Request",
    "_mobile-forgot-password": "Mobile Forgot Password",
    "forgot-password": "Forgot Password",
    "conserve-user-reset-password": "User Reset Password",
    "admin-user-welcome": "Admin User Welcome",
    "delete-admin-user": "Delete Admin User",
    "inform-provider-user-worker-invitation-accepted": "Worker Invitation Accepted Notification",
    "invite-client-csv-new-provider": "Invite New Provider via CSV",
    "invite-client-csv-provider": "Invite Provider via CSV",
    "invite-client-provider": "Invite Client Provider",
    "invite-conserve-user": "Invite Conserve User",
    "invite-org-user": "Invite Organisation User",
    "invite-provider-accept": "Invite Provider Accept",
    "invite-provider-reject": "Invite Provider Reject",
    "invite-provider-worker": "Invite Provider Worker",
    "provider-certificate-email": "Provider Certificate Email",
    "provider-doc-appr-action-to-admin": "Provider Document Approval Action to Admin",
    "provider-doc-approved-to-admin": "Provider Document Approved to Admin",
    "provider-doc-reject-to-admin": "Provider Document Reject to Admin",
    "provider-doc-validate-to-client": "Provider Document Validate to Client",
    "provider-signup-payment": "Provider Signup Payment",
    "provider-upcoming-invoice": "Provider Upcoming Invoice",
    "review-client-compliance-reminder": "Review Client Compliance Reminder",
    "review-compliance": "Review Compliance",
    "signup-continue": "Signup Continue",
    "signup-email-verification": "Signup Email Verification",
    "worker-accepted-invite": "Worker Accepted Invite",
    "worker-doc-validate-to-client": "Worker Document Validate to Client",
    "worker-remove-to-admin": "Worker Remove to Admin",
    "worker-remove-to-provider": "Worker Remove to Provider",
    "worker_document_expiry": "Worker Document Expiry",
  };

  const key = action.toLowerCase().trim();
  if (overrides[key]) {
    return overrides[key];
  }

  return action
    .replace(/[_-]+/g, " ")
    .trim()
    .split(/\s+/)
    .map(word => {
      const upperWord = word.toUpperCase();
      if (["2FA", "OTP", "CSV", "ID", "API", "WHS", "TC"].includes(upperWord)) {
        return upperWord;
      }
      return word.charAt(0).toUpperCase() + word.slice(1).toLowerCase();
    })
    .join(" ");
};

const formatRoleName = (role, triggeredByUserId) => {
  if (!triggeredByUserId && (!role || role.toLowerCase() === "system")) {
    return "System Generated";
  }
  if (!role) return "-";

  const overrides = {
    "super admin": "Super Admin",
    "client": "Client User",
    "provider": "Provider User",
    "worker": "Worker",
    "system": "System Generated"
  };

  const key = role.toLowerCase().trim();
  if (overrides[key]) {
    return overrides[key];
  }

  return role
    .replace(/[_-]+/g, " ")
    .trim()
    .split(/\s+/)
    .map(word => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
    .join(" ");
};


const { Tr, Th, Td, THead, TBody } = Table;

const EmailAuditLogList = ({ emailData, handleSort, filtersort, filterorder }) => {
  const [dialogIsOpen, setIsOpen] = useState(false);
  const [selectedLog, setSelectedLog] = useState(null);

  const openDialog = (log) => {
    setSelectedLog(log);
    setIsOpen(true);
  };

  const onDialogClose = () => {
    setIsOpen(false);
    setSelectedLog(null);
  };

  const getStatusBadgeClass = (status) => {
    switch (status?.toUpperCase()) {
      case "SENT":
        return "bg-green-100 text-green-800 px-2 py-0.5 rounded-full text-xs font-medium";
      case "PENDING":
        return "bg-yellow-100 text-yellow-800 px-2 py-0.5 rounded-full text-xs font-medium";
      case "FAILED":
        return "bg-red-100 text-red-800 px-2 py-0.5 rounded-full text-xs font-medium";
      default:
        return "bg-gray-100 text-gray-800 px-2 py-0.5 rounded-full text-xs font-medium";
    }
  };

  const getCleanEmailBody = (body) => {
    if (!body) return "";
    const backendUrl = process.env.REACT_APP_API_URL_DEV || "http://localhost:3001";
    return body.replace(/http:\/\/localhost:\d+\/*email\//g, `${backendUrl}/email/`);
  };

  return (
    <React.Fragment>
      <div className="overflow-x-auto border rounded-lg border-gray-200">
        <Table style={{ minWidth: "1250px" }}>
          <THead>
            <Tr>
              <Th>
                <span
                  className="flex items-center gap-1 cursor-pointer"
                  onClick={() => handleSort("created_date")}
                >
                  Timestamp
                  <svg
                    className={`transform ${
                      filtersort === "created_date" && filterorder === "asc"
                        ? "rotate-180"
                        : ""
                    }`}
                    width="17"
                    height="16"
                    viewBox="0 0 17 16"
                    fill="none"
                    xmlns="http://www.w3.org/2000/svg"
                  >
                    <path
                      d="M8.712 3.333v9.334m0 0L13.38 8m-4.667 4.667L4.045 8"
                      stroke="#475467"
                      strokeWidth="1.333"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    />
                  </svg>
                </span>
              </Th>
              <Th>Sender</Th>
              <Th>Recipient</Th>
              <Th>Action</Th>
              <Th>
                <span
                  className="flex items-center gap-1 cursor-pointer"
                  onClick={() => handleSort("subject")}
                >
                  Subject
                  <svg
                    className={`transform ${
                      filtersort === "subject" && filterorder === "asc"
                        ? "rotate-180"
                        : ""
                    }`}
                    width="17"
                    height="16"
                    viewBox="0 0 17 16"
                    fill="none"
                    xmlns="http://www.w3.org/2000/svg"
                  >
                    <path
                      d="M8.712 3.333v9.334m0 0L13.38 8m-4.667 4.667L4.045 8"
                      stroke="#475467"
                      strokeWidth="1.333"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    />
                  </svg>
                </span>
              </Th>
              <Th>Status</Th>
              <Th></Th>
            </Tr>
          </THead>
          <TBody>
            {emailData && emailData.length > 0 ? (
              emailData.map((item) => (
                <Tr key={item.email_audit_log_id}>
                  <Td>
                    {item.created_date
                      ? moment(item.created_date).format("HH:mm DD/MM/YY")
                      : "-"}
                  </Td>
                  <Td>{item.email_from || "-"}</Td>
                  <Td>
                    <span className="truncate max-w-[200px] block" title={item.email_to}>
                      {item.email_to || "-"}
                    </span>
                  </Td>
                  <Td>{formatActionName(item.action_name)}</Td>
                  <Td>
                    <span className="email-subject-wrap block text-sm" style={{ whiteSpace: "normal", wordBreak: "break-word", minWidth: "200px" }} title={item.subject}>
                      {item.subject || "-"}
                    </span>
                  </Td>
                  <Td>
                    <span className={getStatusBadgeClass(item.status)}>
                      {item.status || "PENDING"}
                    </span>
                  </Td>
                  <Td>
                    <span className="flex justify-end">
                      <Button onClick={() => openDialog(item)}>
                        <svg
                          width="20"
                          height="21"
                          viewBox="0 0 20 21"
                          fill="none"
                          xmlns="http://www.w3.org/2000/svg"
                        >
                          <path
                            d="M2.017 11.094c-.114-.18-.17-.27-.202-.408a.977.977 0 0 1 0-.372c.032-.139.088-.229.202-.408.938-1.485 3.73-5.24 7.983-5.24 4.255 0 7.046 3.755 7.984 5.24.113.18.17.27.202.408a.976.976 0 0 1 0 .372c-.032.139-.089.229-.202.408-.938 1.485-3.73 5.24-7.984 5.24s-7.045-3.755-7.983-5.24z"
                            stroke="#667085"
                            strokeWidth="1.667"
                            strokeLinecap="round"
                            strokeLinejoin="round"
                          />
                          <path
                            d="M10 13a2.5 2.5 0 1 0 0-5 2.5 2.5 0 0 0 0 5z"
                            stroke="#667085"
                            strokeWidth="1.667"
                            strokeLinecap="round"
                            strokeLinejoin="round"
                          />
                        </svg>
                      </Button>
                    </span>
                  </Td>
                </Tr>
              ))
            ) : (
              <Tr>
                <Td colSpan={7} className="text-center py-4 text-gray-500">
                  No system email logs found
                </Td>
              </Tr>
            )}
          </TBody>
        </Table>
      </div>

      <Dialog
        isOpen={dialogIsOpen}
        onClose={onDialogClose}
        onRequestClose={onDialogClose}
        width={900}
      >
        <div className="flex border-b mb-5 pb-5 px-6 pt-6 items-center">
          <span className="h-12 w-12 border rounded-lg border-gray-200 flex items-center justify-center shadow-sm mr-4">
            <img src={EmailImg} alt="Email" />
          </span>
          <div>
            <h5 className="mb-0 text-gray-900 text-base font-semibold">
              System Email Log Details
            </h5>
            <p className="text-xs text-gray-500 mb-0">
              Correlation ID: {selectedLog?.correlation_id}
            </p>
          </div>
        </div>
        <div className="p-6 pt-1 max-h-[60vh] overflow-y-auto">
          <div className="grid grid-cols-2 gap-4 mb-4">
            <div>
              <Label className="block text-12 font-medium mb-1">Sender</Label>
              <span className="font-semibold text-black block truncate">
                {selectedLog?.email_from || "-"}
              </span>
            </div>
            <div>
              <Label className="block text-12 font-medium mb-1">Recipient(s)</Label>
              <span className="font-semibold text-black block truncate" title={selectedLog?.email_to}>
                {selectedLog?.email_to || "-"}
              </span>
            </div>
          </div>

          <div className="grid grid-cols-2 gap-4 mb-4">
            <div>
              <Label className="block text-12 font-medium mb-1">CC</Label>
              <span className="font-semibold text-black block truncate">
                {selectedLog?.email_cc || "-"}
              </span>
            </div>
            <div>
              <Label className="block text-12 font-medium mb-1">BCC</Label>
              <span className="font-semibold text-black block truncate">
                {selectedLog?.email_bcc || "-"}
              </span>
            </div>
          </div>

          <div className="grid grid-cols-2 gap-4 mb-4">
            <div>
              <Label className="block text-12 font-medium mb-1">Subject</Label>
              <span className="font-semibold text-black block">
                {selectedLog?.subject || "-"}
              </span>
            </div>
            <div>
              <Label className="block text-12 font-medium mb-1">Timestamp</Label>
              <span className="font-semibold text-black block">
                {selectedLog?.created_date
                  ? moment(selectedLog.created_date).format("HH:mm:ss DD/MM/YYYY")
                  : "-"}
              </span>
            </div>
          </div>

          <div className="grid grid-cols-3 gap-4 mb-4">
            <div>
              <Label className="block text-12 font-medium mb-1">Module</Label>
              <span className="font-semibold text-black block">
                {selectedLog?.module_name || "-"}
              </span>
            </div>
            <div>
              <Label className="block text-12 font-medium mb-1">Action</Label>
              <span className="font-semibold text-black block">
                {formatActionName(selectedLog?.action_name)}
              </span>
            </div>
            <div>
              <Label className="block text-12 font-medium mb-1">Status</Label>
              <span className={getStatusBadgeClass(selectedLog?.status)}>
                {selectedLog?.status || "PENDING"}
              </span>
            </div>
          </div>

          <div className="grid grid-cols-3 gap-4 mb-4">
            <div>
              <Label className="block text-12 font-medium mb-1">Triggered By ID</Label>
              <span className="font-semibold text-black block">
                {selectedLog?.triggered_by_user_id || "-"}
              </span>
            </div>
            <div>
              <Label className="block text-12 font-medium mb-1">Triggered By Role</Label>
              <span className="font-semibold text-black block">
                {formatRoleName(selectedLog?.triggered_by_role, selectedLog?.triggered_by_user_id)}
              </span>
            </div>
            <div>
              <Label className="block text-12 font-medium mb-1">Environment</Label>
              <span className="font-semibold text-black block">
                {selectedLog?.environment || "-"}
              </span>
            </div>
          </div>

          {selectedLog?.failure_reason && (
            <div className="mb-4 bg-red-50 border border-red-200 p-3 rounded-lg">
              <Label className="block text-12 font-medium text-red-700 mb-1">Failure Reason</Label>
              <span className="font-semibold text-red-900 block break-all">
                {selectedLog.failure_reason}
              </span>
            </div>
          )}

          {selectedLog?.attachments && selectedLog?.attachments.length > 0 && (
            <div className="mb-4">
              <Label className="block text-12 font-medium mb-1">Attachments</Label>
              <div className="flex flex-wrap gap-2">
                {selectedLog.attachments.map((att, idx) => (
                  <span
                    key={idx}
                    className="inline-flex items-center gap-1.5 px-3 py-1 rounded bg-gray-100 text-gray-800 text-xs border border-gray-200"
                  >
                    <svg className="w-3.5 h-3.5 text-gray-500" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15.172 7l-6.586 6.586a2 2 0 102.828 2.828l6.414-6.586a4 4 0 00-5.656-5.656l-6.415 6.585a6 6 0 108.486 8.486L20.5 13" />
                    </svg>
                    {att.name} ({Math.round(att.size / 1024)} KB)
                  </span>
                ))}
              </div>
            </div>
          )}

          <div className="mb-4">
            <Label className="block text-12 font-medium mb-1">Email Body</Label>
            <div className="border border-gray-200 rounded-lg bg-white overflow-hidden">
              <iframe
                srcDoc={getCleanEmailBody(selectedLog?.email_body)}
                title="Email Preview"
                className="w-full h-[350px] border-0"
                sandbox="allow-same-origin"
              />
            </div>
          </div>
        </div>
        <div className="mt-8 flex justify-end pb-6 px-6 border-t pt-4">
          <BtnLight onClick={() => onDialogClose()}>Close</BtnLight>
        </div>
      </Dialog>
    </React.Fragment>
  );
};

export default EmailAuditLogList;
